import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Rocket, CheckCircle2, Circle, PartyPopper, CalendarClock, Phone, PhoneCall, CreditCard, ChevronRight } from "lucide-react";
import { Button, StatusBadge, LoadingBlock, toast } from "@/components/ds";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, type ReadinessState } from "@/lib/api";
import { StepShell } from "./_ui";
import type { StepProps } from "./types";

const INTEGRATION_ICON: Record<string, typeof CalendarClock> = {
  calendar: CalendarClock, phone: Phone, voice: PhoneCall, billing: CreditCard,
};

// Real status → badge. Never shows "Connected" unless the backend verified it.
function statusBadge(state?: string): { tone: "green" | "amber" | "blue" | "slate"; label: string } {
  switch (state) {
    case "connected":
    case "active":
      return { tone: "green", label: "Connected" };
    case "setup_required":
      return { tone: "amber", label: "Setup required" };
    case "past_due":
      return { tone: "amber", label: "Past due" };
    case "available":
      return { tone: "blue", label: "Ready to activate" };
    case "optional":
      return { tone: "slate", label: "Optional" };
    default:
      return { tone: "slate", label: "Not connected" };
  }
}

export function StepReadiness({ goBack }: StepProps) {
  const [readiness, setReadiness] = useState<ReadinessState | null>(null);
  const [completing, setCompleting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    api.getReadiness().then(setReadiness).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const complete = async () => {
    setCompleting(true);
    try {
      await api.completeOnboarding();
      await refresh();
      setDone(true);
      toast.success("Your AI front desk is set up!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCompleting(false);
    }
  };

  if (done) {
    return (
      <div className="animate-in fade-in zoom-in-95 duration-500 py-8 text-center" data-testid="onboarding-complete">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100">
          <PartyPopper className="h-8 w-8" />
        </div>
        <h2 className="font-display text-3xl font-semibold tracking-tight text-slate-900">You're all set</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
          Your AI front desk is configured. Connect your phone and calendar next to start taking live calls.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Button onClick={() => navigate("/app/dashboard")} data-testid="go-to-dashboard">Go to dashboard</Button>
          <Button variant="outline" onClick={() => navigate("/app/integrations")} data-testid="go-to-integrations">Connect integrations</Button>
        </div>
      </div>
    );
  }

  if (!readiness) return <LoadingBlock rows={6} />;

  const core = readiness.checks.filter((c) => !c.pending);
  const pending = readiness.checks.filter((c) => c.pending);

  return (
    <StepShell
      icon={Rocket}
      title="You're almost ready"
      description="Here's a snapshot of your setup. Finish the essentials to launch your AI front desk."
      onBack={goBack}
      onContinue={complete}
      saving={completing}
      continueLabel="Complete setup"
      continueDisabled={!readiness.setup_complete}
    >
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Essentials</p>
        <div className="space-y-2" data-testid="readiness-core">
          {core.map((c) => (
            <div key={c.key} className="flex items-center gap-3 rounded-lg border border-slate-200 px-4 py-3" data-testid={`readiness-${c.key}`}>
              {c.done
                ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-500" />
                : <Circle className="h-5 w-5 shrink-0 text-slate-300" />}
              <span className={c.done ? "text-sm text-slate-700" : "text-sm font-medium text-slate-900"}>{c.label}</span>
              {!c.done && <StatusBadge tone="amber" className="ml-auto">To do</StatusBadge>}
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Connect later (optional)</p>
        <p className="mb-3 text-xs text-slate-400">These are optional to launch — you can finish setup now and connect or change them any time from the dashboard.</p>
        <div className="space-y-2" data-testid="readiness-pending">
          {pending.map((c) => {
            const Icon = INTEGRATION_ICON[c.key] ?? Circle;
            const badge = statusBadge(c.state);
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => navigate(c.route ?? "/app/dashboard")}
                className="group flex w-full items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 text-left transition-colors hover:border-blue-300 hover:bg-blue-50/40"
                data-testid={`readiness-${c.key}`}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500 ring-1 ring-slate-100 group-hover:bg-blue-50 group-hover:text-blue-600">
                  <Icon className="h-4.5 w-4.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">{c.label}</span>
                    <StatusBadge tone={badge.tone} data-testid={`readiness-${c.key}-status`}>{badge.label}</StatusBadge>
                  </span>
                  {c.detail && <span className="mt-0.5 block truncate text-xs text-slate-500">{c.detail}</span>}
                </span>
                <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-blue-600" data-testid={`readiness-${c.key}-action`}>
                  {c.action ?? "Configure"} <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {!readiness.setup_complete && (
        <StatusBadge tone="amber" data-testid="readiness-hint">Complete the essentials above to launch</StatusBadge>
      )}
    </StepShell>
  );
}
